import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { PatientRegistrationService } from '../../../service/admin/patient-registration.service';
import { ClinicService } from '../../../service/admin/clinic.service';
import { UserService } from '../../../service/admin/user.service';
import { PrescriptionsService } from '../../../service/admin/prescriptions.service';
import { ReportViewerService, ReportViewerPayload } from '../../../service/admin/report-viewer.service';
import { ReportViewerDialogComponent } from '../../shared/report-viewer/report-viewer-dialog.component';
import { WorkContextService } from '../../../service/admin/work-context.service';
import { WorkContextBarComponent } from '../../shared/work-context-bar/work-context-bar.component';
import { ToasterService } from '../../../service/common/toaster.service';
import { PatientAgeFieldsComponent } from '../../shared/patient-age-fields/patient-age-fields.component';
import {
  formatPatientAge,
  hasValidPatientAge,
  patientAgeToPayload,
  readPatientAge,
} from '../../../utils/patient-age.util';
import { extractHttpErrorMessage } from '../../../utils/http-error.util';

@Component({
  selector: 'app-patient-registration',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    DropdownModule,
    ToastModule,
    ConfirmDialogModule,
    AutoCompleteModule,
    DialogModule,
    WorkContextBarComponent,
    PatientAgeFieldsComponent,
    ReportViewerDialogComponent],

  providers: [ConfirmationService, PatientRegistrationService, ClinicService, PrescriptionsService, WorkContextService],
  templateUrl: './patient-registration.component.html',
  styleUrls: ['./patient-registration.component.scss']
})
export class PatientRegistrationComponent implements OnInit, OnDestroy {
  searchSubject: Subject<string> = new Subject<string>();
  private searchSubscription!: Subscription;

  activeIndex: number = 0;
  patientIndex: number = 0;

  activeSection: number = 0;

  toggleSection(index: number) {
    if (this.activeSection === index) {
      this.activeSection = -1;
    } else {
      this.activeSection = index;
    }
  }

  calculateAge() {
    if (this.patient.dob) {
      const dob = new Date(this.patient.dob);
      const diff = Date.now() - dob.getTime();
      const ageDate = new Date(diff);
      this.patient.age = this.sanitizeAge(Math.abs(ageDate.getUTCFullYear() - 1970));
    }
  }

  public patientList: any[] = [];
  public duplicateGroups: { id: string; reasons: string[]; label: string; patients: any[] }[] = [];
  public showDuplicates = false;
  public duplicatePatientCount = 0;
  public duplicatesLoading = false;
  public duplicatePage = 1;
  public duplicatePageSize = 10;
  public selectedDuplicateIds: number[] = [];
  public openDuplicateGroupIds: Record<string, boolean> = {};
  public deactivatingDuplicates = false;
  public movingVisits = false;
  public statusDialogVisible = false;
  public statusDialogMode: 'deactivate' | 'activate' | 'move' = 'deactivate';
  public statusDialogTitle = '';
  public statusDialogDetail = '';
  private statusDialogAccept: (() => void) | null = null;
  public cardList: any[] = [];
  public filteredCardList: any[] = [];
  public clinicList: any[] = [];
  public doctorsList: any[] = [];
  public showForm: boolean = false;
  public searchTerm: string = '';
  public statusFilter: string = 'all';
  public clinicFilter: string = 'all';
  public freePatientFilter: string = 'all';
  public currentPage: number = 1;
  public pageSize: number = 10;
  readonly pageSizeOptions: number[] = [10, 20, 50];
  readonly maxPatientAge = 130;
  readonly reportMaxFileSizeBytes = 20 * 1024 * 1024;
  readonly statusFilterOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }];
  readonly freePatientFilterOptions = [
    { label: 'All Patients', value: 'all' },
    { label: 'Free only', value: 'free' },
    { label: 'Paid only', value: 'paid' }];

  // New patient toggle flag
  public isNewPatient: boolean = false;

  // Selected card object for autocomplete
  public selectedCard: any = null;

  accordionIndex: number | null = null;


  public patient: any = {
    id: 0,
    card_id: 0,
    // card_number: '',  // Added for new patient manual entry
    clinic_id: 0,
    full_name: '',
    patient_number: '',
    age: '',
    age_months: '',
    age_days: '',
    old_card_number: '',
    contact_number: '',
    email: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    under_dr: '',
    is_free: false,
  };

  public openUploadReportDialog: boolean = false;
  public uploadsByDate: { date: Date, files: any[] }[] = [];
  public pendingFiles: File[] = [];
  public currentPatientId: number = 0;
  public currentPatientName: string = '';
  public isReportDragOver: boolean = false;
  public isUploadingReports: boolean = false;
  public deletingReportId: number | null = null;
  reportViewerVisible = false;
  reportViewerLoading = false;
  reportViewerTitle = '';
  reportViewerIsImage = false;
  reportViewerImageUrl: string | null = null;
  reportViewerDocumentUrl: SafeResourceUrl | null = null;
  reportViewerRawUrl: string | null = null;
  reportViewerError: string | null = null;
  private reportOpenSub?: Subscription;
  public countryOptions: { label: string; value: string }[] = [
    { label: 'India', value: 'India' },
    { label: 'United States', value: 'United States' },
    { label: 'United Kingdom', value: 'United Kingdom' },
    { label: 'Canada', value: 'Canada' },
    { label: 'Australia', value: 'Australia' },
    { label: 'United Arab Emirates', value: 'United Arab Emirates' },
    { label: 'Saudi Arabia', value: 'Saudi Arabia' },
    { label: 'Singapore', value: 'Singapore' },
    { label: 'Malaysia', value: 'Malaysia' },
    { label: 'Thailand', value: 'Thailand' },
    { label: 'Indonesia', value: 'Indonesia' },
    { label: 'Philippines', value: 'Philippines' },
    { label: 'Nepal', value: 'Nepal' },
    { label: 'Bangladesh', value: 'Bangladesh' },
    { label: 'Sri Lanka', value: 'Sri Lanka' },
    { label: 'Pakistan', value: 'Pakistan' },
    { label: 'Japan', value: 'Japan' },
    { label: 'South Korea', value: 'South Korea' },
    { label: 'Germany', value: 'Germany' },
    { label: 'France', value: 'France' },
    { label: 'Italy', value: 'Italy' },
    { label: 'Spain', value: 'Spain' },
    { label: 'Netherlands', value: 'Netherlands' },
    { label: 'Sweden', value: 'Sweden' },
    { label: 'Switzerland', value: 'Switzerland' },
    { label: 'South Africa', value: 'South Africa' },
    { label: 'Nigeria', value: 'Nigeria' },
    { label: 'Kenya', value: 'Kenya' },
    { label: 'Brazil', value: 'Brazil' },
    { label: 'Mexico', value: 'Mexico' },
    { label: 'Argentina', value: 'Argentina' },
    { label: 'Turkey', value: 'Turkey' },
    { label: 'Russia', value: 'Russia' },
    { label: 'China', value: 'China' }
  ];
  public stateOptions: { label: string; value: string }[] = [];
  public cityOptions: { label: string; value: string }[] = [];
  public useManualStateInput: boolean = false;
  public useManualCityInput: boolean = false;

  /** Admin-only: store non-Indian numbers with a dial code (E.164-style). */
  public isForeignContact = false;
  public selectedDialCode = '+1';
  public foreignNationalNumber = '';
  public readonly dialCodeOptions: { label: string; value: string }[] = [
    { label: 'United States / Canada (+1)', value: '+1' },
    { label: 'United Kingdom (+44)', value: '+44' },
    { label: 'United Arab Emirates (+971)', value: '+971' },
    { label: 'Saudi Arabia (+966)', value: '+966' },
    { label: 'Australia (+61)', value: '+61' },
    { label: 'Singapore (+65)', value: '+65' },
    { label: 'Malaysia (+60)', value: '+60' },
    { label: 'Thailand (+66)', value: '+66' },
    { label: 'Indonesia (+62)', value: '+62' },
    { label: 'Philippines (+63)', value: '+63' },
    { label: 'Nepal (+977)', value: '+977' },
    { label: 'Bangladesh (+880)', value: '+880' },
    { label: 'Sri Lanka (+94)', value: '+94' },
    { label: 'Pakistan (+92)', value: '+92' },
    { label: 'Japan (+81)', value: '+81' },
    { label: 'South Korea (+82)', value: '+82' },
    { label: 'Germany (+49)', value: '+49' },
    { label: 'France (+33)', value: '+33' },
    { label: 'Italy (+39)', value: '+39' },
    { label: 'Spain (+34)', value: '+34' },
    { label: 'Netherlands (+31)', value: '+31' },
    { label: 'Sweden (+46)', value: '+46' },
    { label: 'Switzerland (+41)', value: '+41' },
    { label: 'South Africa (+27)', value: '+27' },
    { label: 'Nigeria (+234)', value: '+234' },
    { label: 'Kenya (+254)', value: '+254' },
    { label: 'Brazil (+55)', value: '+55' },
    { label: 'Mexico (+52)', value: '+52' },
    { label: 'Argentina (+54)', value: '+54' },
    { label: 'Turkey (+90)', value: '+90' },
    { label: 'Russia (+7)', value: '+7' },
    { label: 'China (+86)', value: '+86' },
    { label: 'Other / custom (+)', value: '+' },
  ];
  private readonly dialCodesLongestFirst: string[] = this.dialCodeOptions
    .map((option) => option.value.replace(/^\+/, ''))
    .filter((code) => code.length > 0)
    .sort((a, b) => b.length - a.length || a.localeCompare(b));

  private readonly indiaStates: string[] = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
    'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
    'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
    'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
    'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  private readonly indiaCitiesByState: Record<string, string[]> = {
    'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Tirupati', 'Kakinada', 'Rajahmundry', 'Nellore', 'Kurnool', 'Kadapa', 'Anantapur', 'Eluru', 'Ongole'],
    'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Pasighat', 'Tawang', 'Ziro', 'Bomdila', 'Tezu'],
    'Assam': ['Guwahati', 'Dibrugarh', 'Silchar', 'Jorhat', 'Nagaon', 'Tezpur', 'Tinsukia', 'Bongaigaon', 'Sivasagar'],
    'Bihar': ['Patna', 'Gaya', 'Muzaffarpur', 'Bhagalpur', 'Darbhanga', 'Purnia', 'Arrah', 'Begusarai', 'Katihar', 'Munger'],
    'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Durg', 'Korba', 'Rajnandgaon', 'Jagdalpur', 'Raigarh', 'Ambikapur'],
    'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda', 'Bicholim'],
    'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Junagadh', 'Gandhinagar', 'Anand', 'Navsari', 'Morbi', 'Mehsana'],
    'Haryana': ['Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Hisar', 'Karnal', 'Rohtak', 'Yamunanagar', 'Sonipat', 'Panchkula', 'Bhiwani'],
    'Himachal Pradesh': ['Shimla', 'Dharamshala', 'Solan', 'Mandi', 'Baddi', 'Kullu', 'Hamirpur', 'Chamba', 'Una'],
    'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Hazaribagh', 'Deoghar', 'Giridih', 'Ramgarh', 'Chaibasa'],
    'Karnataka': ['Bengaluru', 'Mysuru', 'Mangaluru', 'Hubballi', 'Belagavi', 'Davanagere', 'Shivamogga', 'Tumakuru', 'Ballari', 'Vijayapura', 'Udupi', 'Kalaburagi'],
    'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kannur', 'Kollam', 'Alappuzha', 'Palakkad', 'Malappuram', 'Kottayam'],
    'Madhya Pradesh': ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Ujjain', 'Sagar', 'Satna', 'Ratlam', 'Rewa', 'Dewas', 'Burhanpur'],
    'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Thane', 'Navi Mumbai', 'Aurangabad', 'Solapur', 'Kolhapur', 'Amravati', 'Sangli', 'Jalgaon', 'Nanded'],
    'Manipur': ['Imphal', 'Thoubal', 'Bishnupur', 'Churachandpur'],
    'Meghalaya': ['Shillong', 'Tura', 'Jowai', 'Nongstoin'],
    'Mizoram': ['Aizawl', 'Lunglei', 'Champhai', 'Kolasib'],
    'Nagaland': ['Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha'],
    'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur', 'Puri', 'Balasore', 'Baripada', 'Jharsuguda'],
    'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Mohali', 'Hoshiarpur', 'Pathankot', 'Moga', 'Firozpur'],
    'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner', 'Alwar', 'Bharatpur', 'Sikar', 'Pali', 'Bhilwara'],
    'Sikkim': ['Gangtok', 'Namchi', 'Gyalshing', 'Mangan'],
    'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Erode', 'Vellore', 'Thoothukudi', 'Dindigul', 'Thanjavur', 'Nagercoil'],
    'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam', 'Ramagundam', 'Mahbubnagar', 'Suryapet', 'Adilabad'],
    'Tripura': ['Agartala', 'Udaipur', 'Dharmanagar', 'Kailashahar'],
    'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Noida', 'Ghaziabad', 'Varanasi', 'Prayagraj', 'Agra', 'Meerut', 'Bareilly', 'Aligarh', 'Moradabad', 'Gorakhpur', 'Jhansi', 'Saharanpur'],
    'Uttarakhand': ['Dehradun', 'Haridwar', 'Haldwani', 'Rishikesh', 'Roorkee', 'Kashipur', 'Rudrapur', 'Almora'],
    'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Siliguri', 'Asansol', 'Bardhaman', 'Kharagpur', 'Malda', 'Baharampur', 'Haldia'],
    'Andaman and Nicobar Islands': ['Port Blair'],
    'Chandigarh': ['Chandigarh'],
    'Dadra and Nagar Haveli and Daman and Diu': ['Daman', 'Silvassa', 'Diu'],
    'Delhi': ['New Delhi', 'Delhi', 'Dwarka', 'Rohini', 'Saket', 'Karol Bagh'],
    'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla', 'Udhampur'],
    'Ladakh': ['Leh', 'Kargil'],
    'Lakshadweep': ['Kavaratti', 'Agatti'],
    'Puducherry': ['Puducherry', 'Karaikal', 'Mahe', 'Yanam']
  };

  private readonly indiaAdditionalCitiesByState: Record<string, string[]> = {
    'Andhra Pradesh': ['Amaravati', 'Vizianagaram', 'Srikakulam', 'Machilipatnam', 'Chittoor', 'Tenali', 'Proddatur'],
    'Arunachal Pradesh': ['Roing', 'Aalo', 'Seppa', 'Khonsa'],
    'Assam': ['Karimganj', 'Goalpara', 'Dhubri', 'Diphu', 'North Lakhimpur'],
    'Bihar': ['Samastipur', 'Siwan', 'Bettiah', 'Motihari', 'Buxar', 'Sasaram'],
    'Chhattisgarh': ['Dhamtari', 'Kanker', 'Mahasamund', 'Kawardha'],
    'Goa': ['Valpoi', 'Curchorem'],
    'Gujarat': ['Porbandar', 'Surendranagar', 'Nadiad', 'Palanpur', 'Bharuch', 'Valsad'],
    'Haryana': ['Sirsa', 'Jind', 'Kaithal', 'Rewari', 'Bahadurgarh'],
    'Himachal Pradesh': ['Bilaspur', 'Nahan', 'Palampur', 'Kangra'],
    'Jharkhand': ['Koderma', 'Dumka', 'Godda', 'Palamu', 'Lohardaga'],
    'Karnataka': ['Raichur', 'Bidar', 'Hassan', 'Gadag', 'Koppal', 'Chitradurga'],
    'Kerala': ['Kasaragod', 'Pathanamthitta', 'Idukki', 'Ernakulam'],
    'Madhya Pradesh': ['Chhindwara', 'Katni', 'Singrauli', 'Vidisha', 'Shivpuri'],
    'Maharashtra': ['Akola', 'Latur', 'Dhule', 'Ahmednagar', 'Chandrapur', 'Satara', 'Parbhani'],
    'Manipur': ['Kakching', 'Senapati'],
    'Meghalaya': ['Baghmara', 'Williamnagar'],
    'Mizoram': ['Serchhip', 'Saiha'],
    'Nagaland': ['Zunheboto', 'Phek'],
    'Odisha': ['Angul', 'Jajpur', 'Kendrapara', 'Keonjhar', 'Koraput'],
    'Punjab': ['Kapurthala', 'Abohar', 'Barnala', 'Sangrur'],
    'Rajasthan': ['Chittorgarh', 'Tonk', 'Barmer', 'Nagaur', 'Jaisalmer'],
    'Sikkim': ['Rangpo', 'Singtam'],
    'Tamil Nadu': ['Namakkal', 'Karur', 'Tiruppur', 'Sivakasi', 'Kanchipuram', 'Hosur'],
    'Telangana': ['Nalgonda', 'Mancherial', 'Siddipet', 'Jagtial', 'Kamareddy'],
    'Tripura': ['Belonia', 'Khowai'],
    'Uttar Pradesh': ['Mathura', 'Firozabad', 'Ayodhya', 'Muzaffarnagar', 'Rampur', 'Basti'],
    'Uttarakhand': ['Pithoragarh', 'Tehri', 'Kotdwar'],
    'West Bengal': ['Darjeeling', 'Raiganj', 'Krishnanagar', 'Medinipur', 'Bankura'],
    'Andaman and Nicobar Islands': ['Diglipur'],
    'Dadra and Nagar Haveli and Daman and Diu': ['Amli'],
    'Delhi': ['Janakpuri', 'Lajpat Nagar', 'Mayur Vihar'],
    'Jammu and Kashmir': ['Pulwama', 'Kupwara', 'Kathua'],
    'Ladakh': ['Nubra'],
    'Lakshadweep': ['Amini'],
    'Puducherry': ['Oulgaret']
  };

  constructor(
    private toaster: ToasterService, private confirmationService: ConfirmationService,
    private patientRegistrationService: PatientRegistrationService,
    private clinicService: ClinicService,
    private userService: UserService,
    private prescriptionsService: PrescriptionsService,
    private workContext: WorkContextService,
    private reportViewerService: ReportViewerService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(800),
      distinctUntilChanged()
    ).subscribe(() => {
      if (this.showDuplicates) {
        return;
      }
      this.currentPage = 1;
      this.getPatient();
    });

    this.initializeLocationSelectors();
    this.isLoading = true;

    this.workContext.ensureOptionsLoaded().subscribe({
      next: () => {
        this.clinicList = this.workContext.getClinics().map((clinic: any) => ({
          label: clinic.label,
          value: clinic.value,
          name: clinic.label,
          id: clinic.value
        }));
        this.doctorsList = this.workContext.getDoctors();
        this.hydrateClinicNames();
      },
      error: (err) => console.error('Error fetching work context options:', err)
    });

    this.getPatient();
  }

  public totalRecords: number = 0;
  public totalPages: number = 1;
  public isLoading: boolean = true;
  public duplicateWarning: string | null = null;
  public isCheckingDuplicate: boolean = false;
  private searchTimeout: any;
  private duplicateCheckTimeout: any;
  private duplicateCheckRequestId = 0;
  private cardSearchRequestId = 0;
  private duplicatesPrefetched = false;

  private getPatientParams() {
    return {
      page: this.currentPage,
      per_page: this.pageSize,
      search: (this.searchTerm || '').trim(),
      status: this.statusFilter || 'all',
      clinic_id: this.clinicFilter || 'all',
      is_free: this.freePatientFilter || 'all',
      include_dues: '0'
    };
  }

  getPatient() {
    if (this.showDuplicates) {
      this.loadDuplicates();
      return;
    }
    this.isLoading = true;
    this.patientRegistrationService.getPatient(this.getPatientParams()).subscribe({
      next: (res: any) => {
        this.patientList = res?.data ?? [];
        this.totalRecords = res?.pagination?.total ?? 0;
        this.totalPages = res?.pagination?.last_page ?? 1;
        this.currentPage = res?.pagination?.current_page ?? 1;
        this.hydrateClinicNames();
        this.isLoading = false;
        this.prefetchDuplicates();
      },
      error: (err) => {
        console.error('Error fetching patients:', err);
        this.isLoading = false;
      }
    });
  }

  private setSelectedCardFromPatient(): void {
    const cardId = Number(this.patient?.card_id || 0);
    if (!cardId) {
      this.selectedCard = null;
      return;
    }

    const found = this.findCachedCard(cardId);
    if (found) {
      this.selectedCard = found;
      return;
    }

    if (this.patient?.card_number) {
      this.selectedCard = this.mapCardOption({
        id: cardId,
        number: this.patient.card_number,
        patients: [],
        patient_count: 0,
      });
    }

    this.patientRegistrationService.getCard(cardId).subscribe({
      next: (res: any) => {
        if (Number(this.patient?.card_id) !== cardId) {
          return;
        }
        if (res?.data) {
          this.selectedCard = this.mapCardOption(res.data);
        }
      },
      error: () => {
        if (Number(this.patient?.card_id) !== cardId) {
          return;
        }
        this.selectedCard = this.patient?.card_number
          ? this.mapCardOption({
              id: cardId,
              number: this.patient.card_number,
              patients: [],
              patient_count: 0,
            })
          : null;
      }
    });
  }

  private findCachedCard(cardId: number): any | null {
    return this.cardList.find((card) => Number(card.value) === cardId)
      || this.filteredCardList.find((card) => Number(card.value) === cardId)
      || null;
  }

  getClinic() {
    this.clinicService.getClinic().subscribe({
      next: (res: any) => {
        this.clinicList = Array.isArray(res.data)
          ? res.data.map((clinic: any) => ({
            label: clinic.name,
            value: clinic.id
          }))
          : [];
        this.hydrateClinicNames();
      },
      error: (err) => {
        console.error('Clinic API error:', err);
        this.clinicList = [];
      }
    });
  }

  private hydrateClinicNames(): void {
    if (!this.patientList?.length || !this.clinicList?.length) {
      return;
    }

    const clinicMap = new Map<number, string>();
    this.clinicList.forEach((c: any) => clinicMap.set(Number(c.value), c.label));

    this.patientList = this.patientList.map((patient: any) => ({
      ...patient,
      clinic_name: patient.clinic_name || clinicMap.get(Number(patient.clinic_id)) || ''
    }));
    this.hydrateDuplicateClinicNames(clinicMap);
  }

  toggleDuplicates(): void {
    this.showDuplicates = !this.showDuplicates;
    if (this.showDuplicates) {
      this.loadDuplicates();
      return;
    }
    this.currentPage = 1;
    this.getPatient();
  }

  get visibleDuplicateGroups(): { id: string; reasons: string[]; label: string; patients: any[] }[] {
    const term = (this.searchTerm || '').trim().toLowerCase();
    if (!term) {
      return this.duplicateGroups;
    }

    return this.duplicateGroups
      .map((group) => ({
        ...group,
        patients: group.patients.filter((patient) => this.patientMatchesSearch(patient, group, term)),
      }))
      .filter((group) => group.patients.length > 0);
  }

  get pagedDuplicateGroups(): { id: string; reasons: string[]; label: string; patients: any[] }[] {
    const start = (this.duplicatePageSafe - 1) * this.duplicatePageSize;
    return this.visibleDuplicateGroups.slice(start, start + this.duplicatePageSize);
  }

  get duplicateTotalPages(): number {
    return Math.max(1, Math.ceil(this.visibleDuplicateGroups.length / this.duplicatePageSize));
  }

  get duplicatePageSafe(): number {
    return Math.min(Math.max(this.duplicatePage, 1), this.duplicateTotalPages);
  }

  get duplicateVisiblePages(): number[] {
    const radius = 2;
    const current = this.duplicatePageSafe;
    const start = Math.max(1, current - radius);
    const end = Math.min(this.duplicateTotalPages, current + radius);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  duplicateReasonLabel(reason: string): string {
    const labels: Record<string, string> = {
      name: 'Same name',
      phone: 'Same phone',
      email: 'Same email',
      card: 'Same card',
    };
    return labels[reason] || reason;
  }

  duplicateReasonText(reasons: string[] | null | undefined): string {
    return (reasons || []).map((reason) => this.duplicateReasonLabel(reason)).join(' · ') || 'Possible duplicate';
  }

  primaryPatient(group: { patients: any[] }): any | null {
    return group.patients.find((patient) => patient?.is_primary) || null;
  }

  previewCopies(group: { patients: any[] }): any[] {
    return group.patients.filter((patient) => !patient?.is_primary).slice(0, 2);
  }

  hiddenCopyCount(group: { patients: any[] }): number {
    return Math.max(0, group.patients.filter((patient) => !patient?.is_primary).length - 2);
  }

  patientGlance(item: any): string {
    return [item?.patient_number, item?.contact_number, item?.clinic_name].filter(Boolean).join(' · ') || '—';
  }

  visitCount(item: any): number {
    return Number(item?.visit_count) || 0;
  }

  prescriptionCount(item: any): number {
    return Number(item?.prescription_count) || 0;
  }

  hasVisitStats(item: any): boolean {
    return this.visitCount(item) > 0 || this.prescriptionCount(item) > 0;
  }

  visitSummary(item: any): string {
    const visits = this.visitCount(item);
    const prescriptions = this.prescriptionCount(item);
    if (!visits && !prescriptions) {
      return '';
    }
    const visitLabel = visits === 1 ? '1 visit' : `${visits} visits`;
    const prescriptionLabel = prescriptions === 1 ? '1 prescription' : `${prescriptions} prescriptions`;
    return `${visitLabel} · ${prescriptionLabel}`;
  }

  canMoveVisits(item: any): boolean {
    if (!item || item.is_primary) {
      return false;
    }
    return this.hasVisitStats(item);
  }

  confirmMoveVisits(item: any, event?: Event): void {
    event?.stopPropagation();
    if (!this.canMoveVisits(item) || this.movingVisits) {
      return;
    }

    const group = this.duplicateGroups.find((entry) =>
      entry.patients.some((patient) => Number(patient.id) === Number(item.id)),
    );
    const keep = group ? this.primaryPatient(group) : null;
    if (!keep?.id) {
      this.toaster.error('The original record for this group could not be found.');
      return;
    }

    const copyName = this.getDisplayName(item);
    const keepName = this.getDisplayName(keep);
    this.openStatusDialog({
      mode: 'move',
      title: `Move visits to ${keepName}`,
      detail: `${this.visitSummary(item)} on ${copyName} will be filed under ${keepName} (${keep.patient_number || 'the kept record'}). Reports and payments on the copy move with them. The copy record stays, without those visits.`,
      accept: () => this.moveVisits(Number(item.id), Number(keep.id)),
    });
  }

  isDuplicateGroupOpen(group: { id: string }): boolean {
    return !!this.openDuplicateGroupIds[group.id];
  }

  toggleDuplicateGroup(group: { id: string }, event?: Event): void {
    event?.stopPropagation();
    this.openDuplicateGroupIds = {
      ...this.openDuplicateGroupIds,
      [group.id]: !this.openDuplicateGroupIds[group.id],
    };
  }

  isDuplicateSelected(item: any): boolean {
    return this.selectedDuplicateIds.includes(Number(item?.id));
  }

  toggleDuplicateSelection(item: any, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const id = Number(item?.id);
    if (!id || !this.isPatientActive(item)) {
      return;
    }
    this.selectedDuplicateIds = checked
      ? Array.from(new Set([...this.selectedDuplicateIds, id]))
      : this.selectedDuplicateIds.filter((selected) => selected !== id);
  }

  isGroupFullySelected(group: { patients: any[] }): boolean {
    const copyIds = this.copyPatientIds(group);
    return copyIds.length > 0 && copyIds.every((id) => this.selectedDuplicateIds.includes(id));
  }

  groupActiveCount(group: { patients: any[] }): number {
    return this.copyPatientIds(group).length;
  }

  toggleGroupSelection(group: { patients: any[] }, event: Event): void {
    event.stopPropagation();
    const checked = (event.target as HTMLInputElement).checked;
    const copyIds = this.copyPatientIds(group);
    if (checked) {
      this.selectedDuplicateIds = Array.from(new Set([...this.selectedDuplicateIds, ...copyIds]));
      return;
    }
    const remove = new Set(copyIds);
    this.selectedDuplicateIds = this.selectedDuplicateIds.filter((id) => !remove.has(id));
  }

  clearDuplicateSelection(): void {
    this.selectedDuplicateIds = [];
  }

  confirmDeactivateSelected(): void {
    const count = this.selectedDuplicateIds.length;
    if (!count || this.deactivatingDuplicates) {
      return;
    }
    this.openStatusDialog({
      mode: 'deactivate',
      title: count === 1 ? 'Deactivate 1 patient' : `Deactivate ${count} patients`,
      detail: 'They leave active lists and search. You can turn a record back on from this page.',
      accept: () => this.deactivateSelectedPatients(),
    });
  }

  goToDuplicatePage(page: number): void {
    this.duplicatePage = Math.min(Math.max(page, 1), this.duplicateTotalPages);
  }

  onDuplicatePageSizeChanged(): void {
    this.duplicatePage = 1;
  }

  private copyPatientIds(group: { patients: any[] }): number[] {
    return group.patients
      .filter((patient) => this.isPatientActive(patient) && !patient.is_primary)
      .map((patient) => Number(patient.id))
      .filter((id) => id > 0);
  }

  private activePatientIds(group: { patients: any[] }): number[] {
    return group.patients
      .filter((patient) => this.isPatientActive(patient))
      .map((patient) => Number(patient.id))
      .filter((id) => id > 0);
  }

  private deactivateSelectedPatients(): void {
    const ids = [...this.selectedDuplicateIds];
    if (!ids.length) {
      return;
    }
    this.deactivatingDuplicates = true;
    this.patientRegistrationService.deactivatePatients(ids).subscribe({
      next: (res: any) => {
        this.deactivatingDuplicates = false;
        this.selectedDuplicateIds = [];
        this.toaster.success(res?.message || 'Patients deactivated.');
        this.getPatient();
      },
      error: (err) => {
        this.deactivatingDuplicates = false;
        this.toaster.error(extractHttpErrorMessage(err, 'Could not deactivate the selected patients.'));
      },
    });
  }

  private prefetchDuplicates(): void {
    if (this.duplicatesPrefetched || this.showDuplicates) {
      return;
    }
    this.duplicatesPrefetched = true;
    this.patientRegistrationService.getDuplicatePatients().pipe(
      catchError(() => of(null))
    ).subscribe((res) => this.applyDuplicateResponse(res));
  }

  private loadDuplicates(): void {
    this.duplicatesLoading = true;
    this.duplicatesPrefetched = true;
    this.patientRegistrationService.getDuplicatePatients().subscribe({
      next: (res: any) => {
        this.duplicatesLoading = false;
        this.applyDuplicateResponse(res);
      },
      error: () => {
        this.duplicatesLoading = false;
        this.duplicateGroups = [];
        this.duplicatePatientCount = 0;
      },
    });
  }

  private applyDuplicateResponse(res: any): void {
    const groups = Array.isArray(res?.data?.groups) ? res.data.groups : [];
    this.duplicateGroups = groups;
    this.duplicatePatientCount = Number(res?.data?.patient_count) || 0;
    this.hydrateDuplicateClinicNames();
  }

  private hydrateDuplicateClinicNames(clinicMap?: Map<number, string>): void {
    const map = clinicMap ?? new Map<number, string>();
    if (!clinicMap) {
      this.clinicList.forEach((clinic: any) => map.set(Number(clinic.value), clinic.label));
    }
    if (!map.size) {
      return;
    }

    this.duplicateGroups = this.duplicateGroups.map((group) => ({
      ...group,
      patients: group.patients.map((patient: any) => ({
        ...patient,
        clinic_name: patient.clinic_name || map.get(Number(patient.clinic_id)) || '',
      })),
    }));
  }

  private patientMatchesSearch(patient: any, group: { label: string; reasons?: string[] }, term: string): boolean {
    const haystack = [
      this.getDisplayName(patient),
      patient?.patient_number,
      patient?.contact_number,
      patient?.email,
      patient?.old_card_number,
      patient?.clinic_name,
      group.label,
      this.duplicateReasonText(group.reasons),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(term);
  }

  getDoctors() {
    this.userService.getDoctors().subscribe({
      next: (res: any) => {
        this.doctorsList = Array.isArray(res.data)
          ? res.data.map((doctor: any) => ({
            label: doctor.name,
            value: doctor.id
          }))
          : [];
      },
      error: (err) => {
        console.error('Doctor API error:', err);
        this.doctorsList = [];
      }
    });
  }

  ngOnDestroy(): void {
    this.reportOpenSub?.unsubscribe();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  // Fetch basic details for creating new patient toggle change
  setCardMode(createNewCard: boolean): void {
    if (this.isNewPatient === createNewCard) {
      return;
    }

    this.isNewPatient = createNewCard;
    this.onNewPatientToggle();
  }

  onNewPatientToggle(): void {
    if (this.isNewPatient) {
      // Clear card_id when creating new patient
      this.patient.card_id = 0;
      this.selectedCard = null;
    } else {
      // Clear card_number when selecting existing card
      this.patient.card_number = '';
    }
  }

  searchCards(event: any): void {
    const query = (event.query || '').trim();
    if (!query) {
      this.filteredCardList = [];
      return;
    }

    const requestId = ++this.cardSearchRequestId;
    this.patientRegistrationService.searchCards(query).subscribe({
      next: (res: any) => {
        if (requestId !== this.cardSearchRequestId) {
          return;
        }
        this.filteredCardList = Array.isArray(res.data)
          ? res.data.map((card: any) => this.mapCardOption(card))
          : [];
      },
      error: () => {
        if (requestId !== this.cardSearchRequestId) {
          return;
        }
        this.filteredCardList = [];
      }
    });
  }

  private mapCardOption(card: any): any {
    const patients = Array.isArray(card.patients) ? card.patients : [];
    const patientNames = patients.map((patient: any) => patient.full_name).filter(Boolean);

    return {
      label: card.number,
      value: card.id,
      number: card.number,
      patientCount: card.patient_count ?? patients.length,
      patients,
      searchText: [card.number, ...patientNames, card.patient_names]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    };
  }

  get selectedCardPatients(): any[] {
    return this.selectedCard?.patients || [];
  }

  get hasValidCardOption(): boolean {
    if (this.patient?.id) {
      return this.isNewPatient || !!(this.selectedCard?.value || Number(this.patient?.card_id) > 0);
    }

    return this.isNewPatient || !!this.selectedCard?.value;
  }

  // Handle card selection from autocomplete
  // onCardSelect(event: any): void {
  //   // When AutoComplete is bound to an object model, event is the selected object.
  //   // Ensure patient.card_id contains the id number for backend compatibility.
  //   const selected = event ?? this.selectedCard;
  //   this.selectedCard = selected ?? null;
  //   this.patient.card_id = selected ? selected.value : 0;
  // }

  onCardSelect(event: any): void {
    const selected = event?.value ?? null;
    if (!selected) {
      this.selectedCard = null;
      this.patient.card_id = 0;
      return;
    }

    this.selectedCard = selected;
    this.patient.card_id = selected.value;
  }


  updateFullName(): void {
    const patientName = this.patient.patientName ? this.patient.patientName : '';
    this.patient.fullName = patientName;
  }


  closeForm(): void {
    this.clearForm();
    this.showForm = false;
  }

  clearForm(): void {
    const defaultClinicId = Number(sessionStorage.getItem('clinic_id')) || 0;
    const defaultDoctorId = Number(sessionStorage.getItem('doctor_id')) || 0;

    let defaultDoctorName = '';
    if (defaultDoctorId && this.doctorsList && this.doctorsList.length > 0) {
      const doc = this.doctorsList.find(d => Number(d.value) === defaultDoctorId);
      if (doc) {
        defaultDoctorName = doc.label;
      }
    }

    this.patient = {
      id: 0,
      full_name: '',
      patient_number: '',
      age: '',
      age_months: '',
      age_days: '',
      old_card_number: '',
      contact_number: '',
      email: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      clinic_id: defaultClinicId,
      card_id: 0,
      card_number: '',
      under_dr: defaultDoctorName,
      country: '',
      is_free: false
    };
    this.resetContactFields();
    // Reset the toggle and selected card
    this.isNewPatient = false;
    this.selectedCard = null;
    this.clearDuplicateWarning();
    this.initializeLocationSelectors();
  }

  processPatient() {
    if (this.patient?.id) {
      this.updatePatient();
    } else {
      this.addPatient();
    }
  }

  get isIndiaSelected(): boolean {
    return this.normalizeLocationValue(this.patient?.country) === 'india';
  }

  onCountryChange(): void {
    this.patient.country = (this.patient.country || '').trim();
    this.patient.state = '';
    this.patient.city = '';
    this.stateOptions = [];
    this.cityOptions = [];

    if (this.isIndiaSelected) {
      this.stateOptions = this.indiaStates.map((state: string) => ({ label: state, value: state }));
      this.stateOptions.push({ label: 'Other (Type Manually)', value: '__other__' });
      this.useManualStateInput = false;
      this.useManualCityInput = false;
      return;
    }

    this.useManualStateInput = true;
    this.useManualCityInput = true;
  }

  onStateChange(): void {
    if (this.patient.state === '__other__') {
      this.patient.state = '';
      this.useManualStateInput = true;
      this.useManualCityInput = true;
      this.cityOptions = [];
      this.patient.city = '';
      return;
    }

    if (this.useManualStateInput) {
      this.patient.state = (this.patient.state || '').trim();
      this.cityOptions = [];
      this.patient.city = '';
      this.useManualCityInput = true;
      return;
    }

    this.useManualStateInput = false;
    this.patient.state = (this.patient.state || '').trim();
    this.cityOptions = [];
    this.patient.city = '';

    if (!this.isIndiaSelected || !this.patient.state) {
      this.useManualCityInput = true;
      return;
    }

    const cities = this.getCitiesForState(this.patient.state);
    this.cityOptions = cities.map((city: string) => ({ label: city, value: city }));
    this.cityOptions.push({ label: 'Other (Type Manually)', value: '__other__' });
    this.useManualCityInput = false;
  }

  onCityChange(): void {
    if (this.patient.city === '__other__') {
      this.patient.city = '';
      this.useManualCityInput = true;
      return;
    }

    this.patient.city = (this.patient.city || '').trim();
  }

  private initializeLocationSelectors(): void {
    const currentCountry = (this.patient?.country || '').trim();
    const currentState = (this.patient?.state || '').trim();
    const currentCity = (this.patient?.city || '').trim();

    this.stateOptions = [];
    this.cityOptions = [];
    this.useManualStateInput = false;
    this.useManualCityInput = false;

    if (!currentCountry) {
      return;
    }

    if (!this.isIndiaSelected) {
      this.useManualStateInput = true;
      this.useManualCityInput = true;
      return;
    }

    this.stateOptions = this.indiaStates.map((state: string) => ({ label: state, value: state }));
    this.stateOptions.push({ label: 'Other (Type Manually)', value: '__other__' });
    this.useManualStateInput = !!currentState && !this.indiaStates.includes(currentState);

    if (currentState && !this.useManualStateInput) {
      const cities = this.getCitiesForState(currentState);
      this.cityOptions = cities.map((city: string) => ({ label: city, value: city }));
      this.cityOptions.push({ label: 'Other (Type Manually)', value: '__other__' });
      this.useManualCityInput = !!currentCity && !cities.includes(currentCity);
      return;
    }

    this.useManualCityInput = !!currentState || !!currentCity;
  }

  private normalizeLocationValue(value: string): string {
    return (value || '').trim().toLowerCase();
  }

  private getCitiesForState(state: string): string[] {
    const base = this.indiaCitiesByState[state] || [];
    const extra = this.indiaAdditionalCitiesByState[state] || [];
    return Array.from(new Set([...base, ...extra])).sort((a, b) => a.localeCompare(b));
  }


  get canSubmitPatientForm(): boolean {
    const hasValidAge = hasValidPatientAge(
      {
        years: this.patient?.age,
        months: this.patient?.age_months,
        days: this.patient?.age_days,
      },
      this.maxPatientAge,
    );
    return !!(
      (this.patient?.full_name || '').trim() &&
      hasValidAge &&
      this.hasValidContactNumber &&
      Number(this.patient?.clinic_id || 0) > 0 &&
      this.hasValidCardOption &&
      !this.duplicateWarning &&
      !this.isCheckingDuplicate
    );
  }

  get composedForeignContact(): string {
    const dialDigits = this.selectedDialCode.replace(/\D/g, '');
    const national = (this.foreignNationalNumber || '').replace(/\D/g, '');
    if (!dialDigits && !national) {
      return '';
    }
    if (!dialDigits) {
      return national ? `+${national}` : '';
    }
    return national ? `+${dialDigits}${national}` : `+${dialDigits}`;
  }

  get foreignNationalMaxLength(): number {
    const dialLen = this.selectedDialCode.replace(/\D/g, '').length;
    return Math.max(4, 15 - dialLen);
  }

  get hasValidContactNumber(): boolean {
    if (this.isForeignContact) {
      const dialDigits = this.selectedDialCode.replace(/\D/g, '');
      const national = (this.foreignNationalNumber || '').replace(/\D/g, '');
      const total = dialDigits.length + national.length;
      return national.length >= 4 && total >= 8 && total <= 15;
    }
    return /^[6-9]\d{9}$/.test((this.patient?.contact_number || '').trim());
  }

  toggleForeignContact(): void {
    this.isForeignContact = !this.isForeignContact;
    if (this.isForeignContact) {
      const existing = (this.patient?.contact_number || '').replace(/\D/g, '');
      this.patient.contact_number = '';
      if (existing && !/^[6-9]\d{9}$/.test(existing)) {
        this.applyContactNumberToForm(existing.startsWith('+') ? existing : `+${existing}`);
        this.isForeignContact = true;
      } else {
        this.foreignNationalNumber = '';
        if (!this.selectedDialCode) {
          this.selectedDialCode = '+1';
        }
      }
    } else {
      this.foreignNationalNumber = '';
      this.patient.contact_number = '';
    }
    this.onDuplicateFieldsChanged();
  }

  onDialCodeChange(): void {
    const national = (this.foreignNationalNumber || '').replace(/\D/g, '');
    this.foreignNationalNumber = national.slice(0, this.foreignNationalMaxLength);
    this.syncForeignContactToPatient();
    this.onDuplicateFieldsChanged();
  }

  onForeignNationalInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = String(input.value ?? '').replace(/\D/g, '').slice(0, this.foreignNationalMaxLength);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.foreignNationalNumber = digits;
    this.syncForeignContactToPatient();
    this.onDuplicateFieldsChanged();
  }

  onContactNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = this.sanitizeContactNumber(input.value);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.patient.contact_number = digits;
    this.onDuplicateFieldsChanged();
  }

  onAgeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = this.sanitizeAge(input.value);
    const display = sanitized === '' ? '' : String(sanitized);
    if (input.value !== display) {
      input.value = display;
    }
    this.patient.age = sanitized;
  }

  onDuplicateFieldsChanged(): void {
    if (this.duplicateCheckTimeout) {
      clearTimeout(this.duplicateCheckTimeout);
    }

    this.duplicateCheckTimeout = setTimeout(() => {
      this.checkForDuplicatePatient();
    }, 400);
  }

  private checkForDuplicatePatient(): void {
    const fullName = (this.patient?.full_name || '').trim();
    const contactNumber = this.getContactNumberForSave();
    const normalizedContact = contactNumber.replace(/\D/g, '');

    if (!fullName || normalizedContact.length < 8) {
      this.clearDuplicateWarning();
      return;
    }

    const params: any = {
      full_name: fullName,
      contact_number: contactNumber,
    };

    const oldCardNumber = (this.patient?.old_card_number || '').trim();
    if (oldCardNumber) {
      params.old_card_number = oldCardNumber;
    }

    if (this.patient?.id) {
      params.exclude_id = this.patient.id;
    }

    this.isCheckingDuplicate = true;
    const requestId = ++this.duplicateCheckRequestId;

    this.patientRegistrationService.checkDuplicatePatient(params).subscribe({
      next: (res: any) => {
        if (requestId !== this.duplicateCheckRequestId) {
          return;
        }

        this.isCheckingDuplicate = false;
        this.duplicateWarning = res?.duplicate ? (res.message || 'This patient already exists.') : null;
      },
      error: () => {
        if (requestId !== this.duplicateCheckRequestId) {
          return;
        }

        this.isCheckingDuplicate = false;
        this.duplicateWarning = null;
      }
    });
  }

  private clearDuplicateWarning(): void {
    this.duplicateWarning = null;
    this.isCheckingDuplicate = false;
    this.duplicateCheckRequestId++;
    if (this.duplicateCheckTimeout) {
      clearTimeout(this.duplicateCheckTimeout);
      this.duplicateCheckTimeout = null;
    }
  }

  private buildPatientPayload(): any {
    // Clone to avoid mutating the UI model
    const payload: any = {
      ...this.patient,
      contact_number: this.getContactNumberForSave(),
      ...patientAgeToPayload({
        years: this.patient.age,
        months: this.patient.age_months,
        days: this.patient.age_days,
      }),
    };
    delete payload.is_active;
    delete payload.clinic_name;

    // If a card object is selected in the AutoComplete, use its value as card_id.
    if (this.selectedCard && typeof this.selectedCard.value !== 'undefined') {
      payload.card_id = this.selectedCard.value;
    }

    // If card_id is not selected (0, null, undefined, ''), remove it from the payload
    if (!payload.card_id) {
      delete payload.card_id;
    } else {
      // If card_id exists, remove card_number to avoid sending both
      delete payload.card_number;
    }

    // Fees are assigned server-side from card type; omit client value on create.
    delete payload.visit_fees;

    return payload;
  }

  addPatient() {
    const payload = this.buildPatientPayload();

    this.patientRegistrationService.addPatient(payload).subscribe({
      next: (res: any) => {
        if (res.data) {
          this.toaster.success(res.message);
          this.getPatient();
          this.closeForm();
        } else {
          this.toaster.error(res.message || 'Failed to add patient');
        }
      },
      error: (err) => this.handlePatientSaveError(err, 'add')
    });
  }

  updatePatient(): void {
    const payload = this.buildPatientPayload();

    this.patientRegistrationService.updatePatient(payload).subscribe({
      next: (res: any) => {
        if (res.data) {
          this.toaster.success(res.message);
          this.getPatient();
          this.closeForm();
        } else {
          this.toaster.error(res.message || 'Failed to update patient');
        }
      },
      error: (err) => this.handlePatientSaveError(err, 'update')
    });
  }

  private handlePatientSaveError(err: any, action: 'add' | 'update'): void {
    const message = err?.error?.message
      || (action === 'add' ? 'Failed to add patient' : 'Failed to update patient');
    this.toaster.error(message);
  }

  deletePatient(item: any): void {
    this.patientRegistrationService.toggleActive(item.id).subscribe((res: any) => {
      this.toaster.success('Patient status updated successfully');
      this.getPatient();
    });
  }

  startAdd(): void {
    this.clearForm();
    this.activeSection = 0;
    this.setCardMode(false);
    this.showForm = true;
  }

  private sanitizeContactNumber(value: unknown): string {
    return String(value ?? '').replace(/\D/g, '').slice(0, 10);
  }

  private resetContactFields(): void {
    this.isForeignContact = false;
    this.selectedDialCode = '+1';
    this.foreignNationalNumber = '';
  }

  private syncForeignContactToPatient(): void {
    this.patient.contact_number = this.composedForeignContact;
  }

  private getContactNumberForSave(): string {
    if (this.isForeignContact) {
      return this.composedForeignContact;
    }
    return (this.patient?.contact_number || '').trim();
  }

  private applyContactNumberToForm(raw: unknown): void {
    const value = String(raw ?? '').trim();
    this.resetContactFields();

    if (!value) {
      this.patient.contact_number = '';
      return;
    }

    const digits = value.replace(/\D/g, '');

    // Plain Indian mobile (optionally typed with +91 / 91)
    if (/^[6-9]\d{9}$/.test(digits)) {
      this.patient.contact_number = digits;
      return;
    }
    if (/^91[6-9]\d{9}$/.test(digits)) {
      this.patient.contact_number = digits.slice(-10);
      return;
    }

    const matched = this.matchDialCode(digits);
    if (matched) {
      this.isForeignContact = true;
      this.selectedDialCode = `+${matched.code}`;
      this.foreignNationalNumber = matched.national.slice(0, this.foreignNationalMaxLength);
      this.syncForeignContactToPatient();
      return;
    }

    // Unknown international-looking number: treat as foreign with custom dial
    if (digits.length > 10 || value.startsWith('+')) {
      this.isForeignContact = true;
      this.selectedDialCode = '+';
      this.foreignNationalNumber = digits.slice(0, this.foreignNationalMaxLength);
      this.syncForeignContactToPatient();
      return;
    }

    this.patient.contact_number = digits.slice(0, 10);
  }

  private matchDialCode(digits: string): { code: string; national: string } | null {
    if (!digits) {
      return null;
    }
    for (const code of this.dialCodesLongestFirst) {
      if (digits.startsWith(code) && digits.length > code.length) {
        return { code, national: digits.slice(code.length) };
      }
    }
    return null;
  }

  private sanitizeAge(value: unknown): number | '' {
    const digits = String(value ?? '').replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    const parsed = parseInt(digits, 10);
    if (!Number.isFinite(parsed)) {
      return '';
    }
    return Math.min(this.maxPatientAge, Math.max(0, parsed));
  }

  startEdit(item: any): void {
    this.patient = { ...item };
    const ageParts = readPatientAge(item);
    this.patient.age = ageParts.years ?? '';
    this.patient.age_months = ageParts.months ?? '';
    this.patient.age_days = ageParts.days ?? '';
    this.applyContactNumberToForm(item?.contact_number);
    this.isNewPatient = !this.patient.card_id;
    this.setSelectedCardFromPatient();
    this.initializeLocationSelectors();
    this.showForm = true;
    setTimeout(() => this.onDuplicateFieldsChanged(), 0);
  }

  openUploadDialog(item: any): void {
    this.currentPatientId = item.id;
    this.currentPatientName = this.getDisplayName(item);
    this.pendingFiles = [];
    this.isReportDragOver = false;
    this.deletingReportId = null;
    this.prescriptionsService.getReportsByPatientId(item.id).subscribe((rRes: any) => {
      this.applyPatientReports(rRes.success ? rRes.data : []);
    });
    this.openUploadReportDialog = true;
  }

  confirmToggleStatus(item: any): void {
    const isActive = this.isPatientActive(item);
    const patientName = this.getDisplayName(item) || 'this patient';

    this.openStatusDialog({
      mode: isActive ? 'deactivate' : 'activate',
      title: isActive ? `Deactivate ${patientName}` : `Activate ${patientName}`,
      detail: isActive
        ? 'They leave active lists and search. You can turn this record back on from this page.'
        : 'They show up again in patient search and active lists.',
      accept: () => this.deletePatient(item),
    });
  }

  closeStatusDialog(): void {
    this.statusDialogVisible = false;
    this.statusDialogAccept = null;
  }

  acceptStatusDialog(): void {
    const accept = this.statusDialogAccept;
    this.closeStatusDialog();
    accept?.();
  }

  private moveVisits(fromId: number, toId: number): void {
    if (!fromId || !toId || this.movingVisits) {
      return;
    }
    this.movingVisits = true;
    this.patientRegistrationService.mergeDuplicateVisits(fromId, toId).subscribe({
      next: (res: any) => {
        this.movingVisits = false;
        this.toaster.success(res?.message || 'Visits moved to the original patient.');
        this.loadDuplicates();
      },
      error: (err) => {
        this.movingVisits = false;
        this.toaster.error(extractHttpErrorMessage(err, 'Could not move the visits.'));
      },
    });
  }

  private openStatusDialog(options: {
    mode: 'deactivate' | 'activate' | 'move';
    title: string;
    detail: string;
    accept: () => void;
  }): void {
    this.statusDialogMode = options.mode;
    this.statusDialogTitle = options.title;
    this.statusDialogDetail = options.detail;
    this.statusDialogAccept = options.accept;
    this.statusDialogVisible = true;
  }



  isPatientActive(item: any): boolean {
    return item?.is_active === true || item?.is_active === 1 || item?.is_active === '1' || item?.is_active === 'Active';
  }

  isFreePatient(item: any): boolean {
    return item?.is_free === true || item?.is_free === 1 || item?.is_free === '1' || item?.is_free === 'true';
  }

  getPatientInitial(item: any): string {
    const name = this.getDisplayName(item);
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    return parts[0].charAt(0).toUpperCase();
  }

  getDisplayName(item: any): string {
    const salutation = (item?.salutation || '').trim();
    const fullName = (item?.full_name || '').trim();
    return [salutation, fullName].filter(Boolean).join(' ') || '—';
  }

  formatAge(item: any): string {
    if (!item || typeof item === 'object') {
      return formatPatientAge(item?.age, item?.age_months, item?.age_days);
    }
    return formatPatientAge(item);
  }

  getStatusClass(item: any): string {
    return this.isPatientActive(item) ? 'status-active' : 'status-inactive';
  }

  getFreePatientClass(item: any): string {
    return this.isFreePatient(item) ? 'status-free' : 'status-paid';
  }

  get visiblePageNumbers(): number[] {
    const radius = 2;
    const start = Math.max(1, this.currentPage - radius);
    const end = Math.min(this.totalPages, this.currentPage + radius);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  get clinicFilterOptions(): { label: string; value: string }[] {
    return [
      { label: 'All Clinics', value: 'all' },
      ...this.clinicList.map(c => ({
        label: c.name || c.label,
        value: String(c.id ?? c.value),
      }))];
  }

  onSearchChange(): void {
    if (this.showDuplicates) {
      this.duplicatePage = 1;
      return;
    }
    this.searchSubject.next(this.searchTerm);
  }

  onFiltersChanged(): void {
    this.currentPage = 1;
    this.getPatient();
  }

  onFilterDropdownChange(event: { value?: string }, field: 'statusFilter' | 'clinicFilter' | 'freePatientFilter'): void {
    if (event?.value !== undefined && event?.value !== null) {
      this[field] = event.value;
    }
    this.onFiltersChanged();
  }

  onPageSizeChanged(): void {
    this.currentPage = 1;
    this.getPatient();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = 'all';
    this.clinicFilter = 'all';
    this.freePatientFilter = 'all';
    this.currentPage = 1;
    this.getPatient();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.getPatient();
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.getPatient();
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.getPatient();
    }
  }

  resetAccordion() {
    this.accordionIndex = 0;
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) {
      return;
    }

    this.processSelectedFiles(files);
    input.value = '';
  }

  onReportDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isReportDragOver = true;
  }

  onReportDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isReportDragOver = false;
  }

  onReportDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isReportDragOver = false;

    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length) {
      this.processSelectedFiles(files);
    }
  }

  removePendingFile(index: number): void {
    if (index < 0 || index >= this.pendingFiles.length) {
      return;
    }
    this.pendingFiles = this.pendingFiles.filter((_, i) => i !== index);
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  private processSelectedFiles(incoming: File[]) {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    const maxFileSize = this.reportMaxFileSizeBytes;
    const validFiles: File[] = [...this.pendingFiles];
    let addedCount = 0;

    for (const file of incoming) {
      if (!allowedTypes.includes(file.type)) {
        this.toaster.add({
          severity: 'error',
          summary: 'Invalid File Type',
          detail: `File '${file.name}' was rejected. Only PDF, JPG, and PNG are allowed.`
        });
        continue;
      }

      if (file.size > maxFileSize) {
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
        this.toaster.add({
          severity: 'error',
          summary: 'File Too Large',
          detail: `File '${file.name}' (${fileSizeMB} MB) exceeds the 20 MB limit.`
        });
        continue;
      }

      const isDuplicate = validFiles.some(
        existing => existing.name === file.name && existing.size === file.size
      );
      if (isDuplicate) {
        continue;
      }

      validFiles.push(file);
      addedCount++;
    }

    this.pendingFiles = validFiles;

    if (addedCount > 0) {
      this.toaster.add({
        severity: 'info',
        summary: 'Files Ready',
        detail: `${addedCount} file(s) added. Click Upload files when ready.`
      });
    }
  }

  onUploadSelectedFiles() {
    if (!this.pendingFiles || this.pendingFiles.length === 0) {
      this.toaster.warn('Please select valid files before uploading.', 'No Files');
      return;
    }
    if (!this.currentPatientId) {
      this.toaster.warn('Please select a patient before uploading reports.', 'Patient Required');
      return;
    }

    const formData = new FormData();
    formData.append('patient_id', String(this.currentPatientId));
    this.pendingFiles.forEach((file: File) => {
      formData.append('files[]', file, file.name);
    });

    this.isUploadingReports = true;
    const fileCount = this.pendingFiles.length;

    this.prescriptionsService.uploadReports(formData).subscribe({
      next: (uploadRes: any) => {
        this.isUploadingReports = false;
        if (uploadRes?.success) {
          this.toaster.add({
            severity: 'success',
            summary: 'Upload Complete',
            detail: `${fileCount} file(s) uploaded successfully.`
          });
          this.pendingFiles = [];
          this.refreshReports();
          return;
        }

        this.toaster.error(uploadRes?.message || 'There was an error uploading reports.', 'Upload Failed');
      },
      error: () => {
        this.isUploadingReports = false;
        this.toaster.error('There was an error uploading reports. Please try again.', 'Upload Failed');
      }
    });
  }

  reportFileUrl(file: any): string {
    return this.prescriptionsService.resolveReportFileUrl(file);
  }

  openFile(file: any, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (!file) {
      this.toaster.warn('Document is unavailable.', 'Open Failed');
      return;
    }

    this.openReportViaDialog(file);
  }

  private openReportInDialog(file: any, blobOrFileUrl: string): void {
    this.reportViewerVisible = true;
    this.reportViewerLoading = false;
    this.reportViewerTitle = file?.name || 'Document';
    this.reportViewerRawUrl = blobOrFileUrl;

    const isImage = String(file?.type || '').toLowerCase().startsWith('image/');
    this.reportViewerIsImage = isImage;
    this.reportViewerError = null;

    if (isImage) {
      this.reportViewerImageUrl = blobOrFileUrl;
      this.reportViewerDocumentUrl = null;
    } else {
      this.reportViewerImageUrl = null;
      this.reportViewerDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobOrFileUrl);
    }
    this.cdr.markForCheck();
  }

  private openReportViaDialog(file: any): void {
    this.resetReportViewerState();
    this.reportViewerVisible = true;
    this.reportViewerLoading = true;
    this.reportViewerTitle = file?.name || 'Document';
    this.reportViewerIsImage = String(file?.type || '').toLowerCase().startsWith('image/');
    this.cdr.detectChanges();

    this.reportOpenSub = this.reportViewerService.loadReport(file).subscribe({
      next: (payload) => {
        this.reportViewerRawUrl = payload.blobUrl;
        this.applyReportViewerPayload(payload);
      },
      error: (err) => {
        const message = extractHttpErrorMessage(err, 'Unable to open document. Please try again.');
        this.reportViewerLoading = false;
        this.reportViewerError = message;
        this.reportViewerRawUrl = this.reportFileUrl(file) || file?.url || null;
        this.toaster.error(message, 'Open Failed');
        this.cdr.markForCheck();
      },
    });
  }

  private resetReportViewerState(): void {
    this.reportViewerLoading = false;
    this.reportViewerError = null;
    this.reportViewerTitle = '';
    this.reportViewerIsImage = false;
    this.reportViewerImageUrl = null;
    this.reportViewerDocumentUrl = null;
    this.reportViewerRawUrl = null;
  }

  private applyReportViewerPayload(payload: ReportViewerPayload): void {
    this.reportViewerTitle = payload.title;
    this.reportViewerIsImage = payload.isImage;
    this.reportViewerError = null;

    if (payload.isImage) {
      this.reportViewerImageUrl = payload.blobUrl;
      this.reportViewerDocumentUrl = null;
    } else {
      this.reportViewerImageUrl = null;
      this.reportViewerDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(payload.blobUrl);
    }

    this.reportViewerLoading = false;
    this.cdr.markForCheck();
  }

  closeReportViewer(): void {
    this.reportOpenSub?.unsubscribe();
    this.reportViewerVisible = false;
    this.resetReportViewerState();
  }

  private applyPatientReports(data: any[]) {
    this.uploadsByDate = data || [];
  }

  uploadPendingFiles() {
    this.onUploadSelectedFiles();
  }

  private mergeReports(newReports: any[]) {
    newReports.forEach(r => {
      const date = r.uploaded_at.split(' ')[0];
      let group = this.uploadsByDate.find(g => g.date === date);
      if (!group) {
        group = { date, files: [] };
        this.uploadsByDate.push(group);
      }
      group.files.push(r);
    });
  }

  closeUploadReportDialog() {
    this.openUploadReportDialog = false;
    this.pendingFiles = [];
    this.isReportDragOver = false;
    this.currentPatientName = '';
    this.deletingReportId = null;
  }

  deleteReport(file: any, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    const reportId = Number(file?.id || 0);
    if (!reportId) {
      this.toaster.warn('This file cannot be deleted because it is not saved on server.', 'Not Deletable');
      return;
    }

    const reportName = file?.name || 'this report';
    this.confirmationService.confirm({
      key: 'reportDeleteDialog',
      header: 'Delete Report',
      message: `Delete "${reportName}"? This action cannot be undone.`,
      icon: 'pi pi-trash',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptIcon: 'pi pi-trash',
      rejectIcon: 'pi pi-times',
      defaultFocus: 'reject',
      acceptButtonStyleClass: 'report-delete-accept-btn',
      rejectButtonStyleClass: 'report-delete-reject-btn',
      accept: () => this.executeReportDeletion(reportId),
    });
  }

  private executeReportDeletion(reportId: number) {
    if (this.deletingReportId === reportId) {
      return;
    }

    this.deletingReportId = reportId;
    this.prescriptionsService.deleteReports(reportId).subscribe({
      next: (res: any) => {
        this.deletingReportId = null;
        if (!res?.success) {
          this.toaster.error(res?.message || 'Unable to delete document.', 'Delete Failed');
          return;
        }

        this.removeReportFromGallery(reportId);
        this.toaster.success('Document deleted successfully.', 'Deleted');
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.deletingReportId = null;
        const message = extractHttpErrorMessage(err, 'Unable to delete document. Please try again.');
        this.toaster.error(message, 'Delete Failed');
      }
    });
  }

  get totalReportsCount(): number {
    return (this.uploadsByDate || []).reduce((count: number, group: any) => {
      return count + (Array.isArray(group?.files) ? group.files.length : 0);
    }, 0);
  }

  get selectedUploadSizeLabel(): string {
    const totalBytes = (this.pendingFiles || []).reduce((sum: number, file: File) => sum + (file.size || 0), 0);
    if (!totalBytes) return '0 KB';
    if (totalBytes >= 1024 * 1024) {
      return `${(totalBytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${Math.max(1, Math.round(totalBytes / 1024))} KB`;
  }

  getReportDateLabel(value: any): string {
    if (!value) return '';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleDateString('en-US', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  getReportTypeLabel(file: any): string {
    const type = String(file?.type || '').toLowerCase();
    if (type.startsWith('image/')) return 'Image';
    if (type === 'application/pdf') return 'PDF';
    return (file?.name || '').split('.').pop()?.toUpperCase() || 'File';
  }

  private refreshReports() {
    if (!this.currentPatientId) {
      this.uploadsByDate = [];
      return;
    }
    this.prescriptionsService.getReportsByPatientId(this.currentPatientId).subscribe({
      next: (rRes: any) => {
        this.applyPatientReports(rRes?.success ? (rRes.data || []) : []);
      },
      error: () => {
        this.uploadsByDate = [];
      }
    });
  }

  private removeReportFromGallery(reportId: number) {
    this.uploadsByDate = (this.uploadsByDate || [])
      .map((group: any) => ({
        ...group,
        files: (group?.files || []).filter((file: any) => Number(file?.id) !== reportId)
      }))
      .filter((group: any) => (group?.files || []).length > 0);
  }
}
